import { useMemo } from 'react';

import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { GameStatus } from '@pp/core/src/store/slices/game/types';

import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';
import { BUTTON_STATES, BUTTON_STATES_LABEL, ButtonStateType } from '../../GameBetButton/types';

import { useIsBetConfirmedConnected } from './useIsBetConfirmedConnected';

export const useButtonStatusConnected = (betId: number): ButtonStateType => {
  const currentStatus = useGameStateSlice.getCurrentStatus();
  const isBetConfirmed = useIsBetConfirmedConnected(betId);
  const hasEarlyBets = useHighflyerStateSlice.hasEarlyBets(betId);
  const isLowBalance = useBetSlice.isLowBalance();
  const cashedAmount = useHighflyerStateSlice.getCashedAmount(betId);
  const isInDealingInProgress = useHighflyerStateSlice.isDealingInProgress(betId);
  const totalBet = useBetSlice.totalBet();
  const isStatsOpen = useHighflyerGameStateSlice.toggleStats();

  return useMemo(() => {
    let currentButtonState: ButtonStateType = {
      label: BUTTON_STATES_LABEL.confirmBet,
      state: BUTTON_STATES.confirm,
    };
    switch (currentStatus) {
      case GameStatus.LastBets:
      case GameStatus.PlaceYourBets:
      case GameStatus.BetsClosed:
      case GameStatus.BetsRejected:
      case GameStatus.BetsAccepted:
      case GameStatus.GameCancelled:
        if (isBetConfirmed) {
          currentButtonState = {
            label: BUTTON_STATES_LABEL.cancelBet,
            state: BUTTON_STATES.cancel,
          };
        }
        break;
      case GameStatus.WaitForNextGame:
      case GameStatus.Dealing:
        if (hasEarlyBets) {
          currentButtonState = {
            label: BUTTON_STATES_LABEL.cancelBet,
            state: BUTTON_STATES.cancel,
          };
        }
        if (isInDealingInProgress) {
          currentButtonState = {
            label: BUTTON_STATES_LABEL.cashout,
            state: BUTTON_STATES.cashout,
          };
        }
        break;
      case GameStatus.Result:
        if (hasEarlyBets) {
          currentButtonState = {
            label: BUTTON_STATES_LABEL.cancelBet,
            state: BUTTON_STATES.cancel,
          };
        }
        if (isInDealingInProgress) {
          currentButtonState = {
            label: BUTTON_STATES_LABEL.cashout,
            state: BUTTON_STATES.flewAway,
          };
        }
        break;
    }
    if (isLowBalance && totalBet === 0) {
      //This state shouldbe shown on both betboards simultaneously not on either one
      currentButtonState = {
        label: BUTTON_STATES_LABEL.lowBalance,
        state: BUTTON_STATES.lowbalance,
      };
    }
    if (cashedAmount > 0 && isInDealingInProgress) {
      currentButtonState = {
        label: BUTTON_STATES_LABEL.cashed,
        state: BUTTON_STATES.cashed,
      };
    }

    //Special case for Floating betboard
    if (isStatsOpen && cashedAmount > 0) {
      currentButtonState = {
        label: BUTTON_STATES_LABEL.cashed,
        state: BUTTON_STATES.cashed,
      };
    }
    return currentButtonState;
  }, [
    currentStatus,
    isBetConfirmed,
    cashedAmount,
    isLowBalance,
    totalBet,
    isInDealingInProgress,
    isStatsOpen,
    hasEarlyBets,
  ]);
};
